import React, { useState, useEffect, CSSProperties } from 'react';
import { Tag, Dropdown, Empty } from 'antd';
import { CheckOutlined, DownOutlined } from '@ant-design/icons';
import styles from './singleTagSelect.module.scss'

export interface SingleTagSelectOption {
    value: string | number;
    label: string;
    dropdownLabel?: string;
    color?: string;
    icon?: React.ReactNode;
}

export interface SingleTagSelectProps {
    options: SingleTagSelectOption[];
    value?: string | number;
    className?: string;
    containerStyle?: CSSProperties;
    onChange?: (value: string | number, option: SingleTagSelectOption) => void;
    placeholder?: string;
    disabled?: boolean;
    size?: 'small' | 'default' | 'large';
    allowClear?: boolean;
    onTagClick?: (value: string | number, option: SingleTagSelectOption) => void;
    tagStyle?: React.CSSProperties;
    dropdownMaxHeight?: number;
    showCheck?: boolean;
}

const SingleTagSelect: React.FC<SingleTagSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = 'Выберите значение',
    disabled = false,
    size = 'default',
    allowClear = true,
    onTagClick,
    tagStyle,
    dropdownMaxHeight = 300,
    showCheck = false,
    containerStyle,
}) => {
    const [selectedValue, setSelectedValue] = useState<string | number | undefined>(value);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        setSelectedValue(value);
    }, [value]);

    const selectedOption = options.find(opt => opt.value === selectedValue);

    const handleSelect = (option: SingleTagSelectOption) => {
        if (disabled) return;
        setSelectedValue(option.value);
        onChange?.(option.value, option);
        setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;
        setSelectedValue(undefined);
        onChange?.(undefined as any, undefined as any);
    };

    const handleMainTagClick = () => {
        if (disabled) return;
        setIsOpen(!isOpen);
        onTagClick?.(selectedValue as any, selectedOption as any);
    };

    const getSizeClass = (): string => {
        switch (size) {
            case 'small':
                return styles['tag-base-small'];
            case 'large':
                return styles['tag-base-large'];
            default:
                return styles['tag-base-default'];
        }
    };

    const getMainTagClasses = (): string => {
        const classes = [styles['tag-base'], getSizeClass()];
        
        if (disabled) {
            classes.push(styles['tag-base-disabled']);
        } else {
            classes.push(styles['tag-base-clickable']);
        }
        
        return classes.join(' ');
    };

    const getDropdownTagClasses = (isSelected: boolean): string => {
        const classes = [styles['tag-base'], getSizeClass(), styles['dropdown-tag']];
        
        if (isSelected && showCheck) {
            classes.push(styles['tag-selected']);
        }
        
        return classes.join(' ');
    };

    const dropdownContent = () => (
        <div 
            className={styles['dropdown-content']}
            style={{ maxHeight: dropdownMaxHeight }}
        >
            {options.map(option => {
                const isSelected = showCheck && selectedValue === option.value;
                const displayText = option.dropdownLabel || option.label;

                return (
                    <div
                        key={option.value}
                        className={styles['dropdown-item']}
                    >
                        <Tag
                            color={option.color || (isSelected ? 'blue' : 'default')}
                            className={getDropdownTagClasses(isSelected)}
                            onClick={() => handleSelect(option)}
                        >
                            {option.icon && (
                                <span className={styles['option-icon']}>
                                    {option.icon}
                                </span>
                            )}
                            {displayText}
                            {isSelected && (
                                <CheckOutlined className={styles['check-icon']} />
                            )}
                        </Tag>
                    </div>
                );
            })}
            {options.length === 0 && (
                <Empty 
                    description="Нет данных" 
                    image={Empty.PRESENTED_IMAGE_SIMPLE} 
                    style={{ margin: 0 }} 
                />
            )}
        </div>
    );

    const renderMainTag = () => {
        const tagClasses = getMainTagClasses();
        
        if (selectedOption) {
            return (
                <Tag
                    color={selectedOption.color || 'blue'}
                    closable={allowClear && !disabled}
                    onClose={handleClear}
                    onClick={handleMainTagClick}
                    className={tagClasses}
                    style={tagStyle}
                >
                    {selectedOption.icon && (
                        <span className={styles['option-icon']}>
                            {selectedOption.icon}
                        </span>
                    )}
                    {selectedOption.label}
                    <DownOutlined className={styles['down-icon']} />
                </Tag>
            );
        }
        
        return (
            <Tag
                onClick={handleMainTagClick}
                className={`${tagClasses} ${styles['tag-base-placeholder']}`}
                style={tagStyle}
            >
                {placeholder}
                <DownOutlined className={styles['down-icon']} />
            </Tag>
        );
    };

    if (options.length === 0) {
        return (
            <Tag className={getMainTagClasses()} style={tagStyle}>
                <Empty 
                    description="Нет данных" 
                    image={Empty.PRESENTED_IMAGE_SIMPLE} 
                    style={{ margin: 0 }} 
                />
            </Tag>
        );
    }

    return (
        <Dropdown
            open={isOpen}
            onOpenChange={setIsOpen}
            disabled={disabled}
            dropdownRender={dropdownContent}
            trigger={['click']}
            placement="bottom"
            transitionName=""
            getPopupContainer={() => document.body}
        >
            <div
                style={containerStyle}
            >
                {renderMainTag()}
            </div>
        </Dropdown>
    );
};

export default SingleTagSelect;